/**
 * NodeReferencePlugin - 處理節點引用的插件
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemButton,
  Box,
  Typography,
  Divider
} from '@mui/material';
import { $insertNodes, COMMAND_PRIORITY_EDITOR } from 'lexical';
import { $createNodeReferenceNode } from './nodes/NodeReferenceNode';
import { convertIdToAlphanumeric } from '../../utils/idConverter';
import NodePreviewDialog from '../shared/NodePreviewDialog';

export const INSERT_NODE_REFERENCE_COMMAND = 'INSERT_NODE_REFERENCE_COMMAND';

export default function NodeReferencePlugin({ graphNodes }) {
  const [editor] = useLexicalComposerContext();
  const [showNodeSelector, setShowNodeSelector] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [previewNode, setPreviewNode] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  // 註冊插入節點引用的命令
  useEffect(() => {
    return editor.registerCommand(
      INSERT_NODE_REFERENCE_COMMAND,
      () => {
        setShowNodeSelector(true);
        return true;
      },
      COMMAND_PRIORITY_EDITOR
    );
  }, [editor]);

  // 監聽節點引用點擊事件
  useEffect(() => {
    const handleNodeReferenceClick = (event) => {
      const { nodeId } = event.detail;
      const node = graphNodes.find(n => n.id === nodeId);
      if (node) {
        setPreviewNode(node);
        setShowPreview(true);
      }
    };

    document.addEventListener('node-reference-click', handleNodeReferenceClick);
    return () => {
      document.removeEventListener('node-reference-click', handleNodeReferenceClick);
    };
  }, [graphNodes]);

  // 插入節點引用
  const handleInsertReference = useCallback((node) => {
    editor.update(() => {
      const nodeReferenceNode = $createNodeReferenceNode(node.id, node.title || node.data.label);
      $insertNodes([nodeReferenceNode]);
    });
    setShowNodeSelector(false);
    setSearchText('');
  }, [editor]);

  // 過濾節點
  const filteredNodes = graphNodes.filter(node => {
    if (!searchText) return true;
    const searchLower = searchText.toLowerCase();
    const title = (node.title || '').toLowerCase();
    const content = (node.content || '').toLowerCase();
    const nodeIdStr = convertIdToAlphanumeric(node.id).toLowerCase();
    return title.includes(searchLower) || 
           content.includes(searchLower) || 
           nodeIdStr.includes(searchLower);
  });

  return (
    <>
      {/* 節點選擇對話框 */}
      <Dialog
        open={showNodeSelector}
        onClose={() => {
          setShowNodeSelector(false);
          setSearchText('');
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>選擇要引用的節點</DialogTitle>
        <Divider />
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            placeholder="搜尋節點標題、內容或 ID..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{ mb: 2 }}
          />
          
          {filteredNodes.length === 0 ? (
            <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
              找不到符合的節點
            </Typography>
          ) : (
            <List sx={{ maxHeight: '400px', overflow: 'auto' }}>
              {filteredNodes.map((node) => (
                <ListItem key={node.id} disablePadding>
                  <ListItemButton 
                    onClick={() => handleInsertReference(node)}
                    sx={{
                      borderRadius: '8px',
                      mb: 1,
                      '&:hover': {
                        backgroundColor: '#f3f4f6'
                      }
                    }}
                  >
                    <Box sx={{ width: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Box
                          sx={{
                            backgroundColor: 'primary.main',
                            color: 'white',
                            borderRadius: '4px',
                            padding: '2px 6px',
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                            minWidth: '48px',
                            textAlign: 'center'
                          }}
                        >
                          {convertIdToAlphanumeric(node.id)}
                        </Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {node.title || node.data.label || '無標題'}
                        </Typography>
                      </Box>
                      {node.content && (
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{
                            ml: 7,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical'
                          }}
                        >
                          {node.content.replace(/<[^>]*>/g, '').substring(0, 100)}...
                        </Typography>
                      )}
                    </Box>
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowNodeSelector(false);
            setSearchText('');
          }}>
            取消
          </Button>
        </DialogActions>
      </Dialog>

      {/* 節點預覽對話框 */}
      <NodePreviewDialog
        open={showPreview}
        onClose={() => {
          setShowPreview(false);
          setPreviewNode(null);
        }}
        node={previewNode}
        showUserProfile={false}
        titlePrefix="引用節點"
      />
    </>
  );
}
